import { clearCorrelationId } from "./correlationId";
import { writeQueue } from "./storage";

/**
 * Discards analytics state tied to the current user: the pending event queue
 * and the tab's correlation id.
 *
 * Both artifacts outlive a user on a shared workstation. Events below the batch
 * threshold sit in localStorage indefinitely, so user A's queued events would go
 * out on user B's first flush — one POST carrying two users' `userOrgLinkId` and
 * `builderId` under a single `tenantId`. The correlation id lives in
 * sessionStorage, so two sessions in the same tab share one, which both defeats
 * the field's purpose and ties two identities together in the warehouse.
 *
 * Dropping the queue loses the pending events rather than flushing them first.
 * Not because flushing would be wrong — the outgoing user is still the current
 * one at this point, so those events would post under the correct identity — but
 * because it cannot be done reliably from here. `handleLogout` is synchronous and
 * ends in `window.location.assign()`, which cancels any in-flight `fetch`; making
 * it async would not help, since its callers are click handlers that do not await.
 * A flush that lands sometimes and silently fails usually is worse than an honest
 * drop.
 *
 * Preserving them properly means `navigator.sendBeacon()` or
 * `fetch(..., { keepalive: true })`, which survive navigation. Neither is used
 * anywhere in the repo yet, and either would give this function a dependency on
 * `ingestionEndpoint`/`frontendTenantId` that it currently does not need. Worth
 * revisiting if sub-threshold events on logout turn out to matter — note every
 * other exit (tab close, crash, session expiry) already drops them today, so
 * flushing only here would make logout the inconsistent path rather than the
 * correct one.
 *
 * Known gap: this only covers paths that call it. Impersonation switches
 * (`switchAccount` / `switchOwner`) and in-tab session swaps change the user
 * without a logout, and are not wired up. Closing that properly means keying
 * both artifacts by `userOrgLinkId` so a mismatch is structurally impossible —
 * tracked separately.
 */
export function resetAnalyticsIdentity(): void {
  writeQueue([]);
  clearCorrelationId();
}
